import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useStore, uid } from '../state/store';
import { SPECIALISTS } from '../lib/coach';
import { runSpecialist, draftToEvidenceRecord } from '../lib/specialists';
import {
  composeBriefing,
  applyAccept,
  compactAskContext,
  briefingFingerprint,
  recommendationDecision,
  structureAskAnswer,
  mapAskStructured,
} from '../lib/bridge';
import { BRIDGE_ASK_QUESTIONS } from '../lib/constants';
import { orgAskQuestions } from '../lib/orgPlan';
import { Card, Chip, EvidenceBadge, Modal, Area, Banner } from './ui';
import { ExceptionForm } from './Forms';
import { DesignatePrimaryModal, ReplacePrimaryModal } from './PipelineForms';
import { candidateScore, withPrimary, withReplacedPrimary } from '../lib/funnel';
import { newAction, evaluateEligibility } from '../lib/actions';
import { api, fireCrmWriteback } from '../state/api';

function newActionFromText(text, today) {
  return newAction({ text, status: 'Today', selectedForToday: true, source: 'Manual', expectedEvidence: 'E3', workstream: 'Dubai' }, today);
}

const persistedFingerprints = new Map();

export function TheBridge() {
  const { state, d, add, patch, today, set, reload } = useStore();
  const lastCloseout = useMemo(() => {
    const rows = [...(state.closeouts || [])].sort((a, b) => String(b.date).localeCompare(String(a.date)));
    return rows[0] || null;
  }, [state.closeouts]);
  const overrides = state.bridgeRecommendations || [];
  const live = useMemo(
    () => composeBriefing(state, d, { lastCloseout, overrides }),
    [state, d, lastCloseout, overrides],
  );

  const persisted = useRef(false);
  useEffect(() => {
    const fp = live.fingerprint || briefingFingerprint(state);
    if (persistedFingerprints.get(today) === fp) {
      persisted.current = true;
      return;
    }
    const existing = (state.bridgeBriefings || []).find((b) => b.date === today && b.fingerprint === fp);
    if (existing) {
      persisted.current = true;
      persistedFingerprints.set(today, fp);
      return;
    }
    persisted.current = true;
    persistedFingerprints.set(today, fp);
    const briefingId = `brf-${today}-${String(fp).length}`;
    add('bridgeBriefings', {
      id: briefingId,
      date: today,
      payload: live,
      fingerprint: fp,
      scoringConfigVersion: live.scoringConfigVersion,
      generatedAt: new Date().toISOString(),
    });
    for (const rec of live.recommendations || []) {
      const already = (state.bridgeRecommendations || []).find((r) => r.id === rec.id && r.date === today);
      if (already) continue;
      add('bridgeRecommendations', {
        ...rec,
        id: rec.id || uid('rec'),
        briefingId,
        date: today,
        status: rec.status || 'pending',
        scoringConfigVersion: rec.scoringConfigVersion || live.scoringConfigVersion,
      });
    }
  }, [state.bridgeBriefings, today, live, add, state]);

  const briefing = live;
  const storedRecs = (state.bridgeRecommendations || []).filter((r) => r.date === today);
  const recs = storedRecs.length ? storedRecs : live.recommendations || [];
  const pendingPromote = (briefing.top3 || [])
    .map((rec) => recs.find((r) => r.id === rec.id) || rec)
    .filter((r) => r.status === 'pending' || !r.status);
  const pending = recs.filter((r) => r.status === 'pending' || !r.status);
  const canAcceptAll3 = pendingPromote.length === 3;
  const init = briefing.initialization;

  const [askOpen, setAskOpen] = useState(false);
  const [overrideId, setOverrideId] = useState(null);
  const [overrideWhy, setOverrideWhy] = useState('');
  const [overrideReplacement, setOverrideReplacement] = useState('');
  const [overrideError, setOverrideError] = useState('');
  const [rejectId, setRejectId] = useState(null);
  const [rejectWhy, setRejectWhy] = useState('');
  const [modal, setModal] = useState(null);
  const [modalRec, setModalRec] = useState(null);
  const [planStatus, setPlanStatus] = useState(null);
  const [planBusy, setPlanBusy] = useState('');
  const [rollbackId, setRollbackId] = useState('');
  const [rollbackWhy, setRollbackWhy] = useState('');

  const versions = [...(state.planVersions || [])].sort((a, b) =>
    String(b.createdAt || '').localeCompare(String(a.createdAt || '')),
  );
  const currentPlan = versions[0] || null;
  const autopilotIdle = !currentPlan;

  useEffect(() => {
    api.planStatus()
      .then(setPlanStatus)
      .catch(() => setPlanStatus(null));
  }, [state.planVersions, state.accounts, state.opportunities, state.evidence]);

  const runMutations = (mutations) => {
    for (const m of mutations || []) {
      if (m.type === 'patch') patch(m.path, m.id, m.changes);
      if (m.type === 'add') add(m.path, { ...m.item, id: m.item.id || uid('t3') });
    }
  };

  const logDecision = (rec, status, founderWhy = '', replacementMoveId = '') => {
    add('recommendationDecisions', {
      id: uid('rd'),
      ...recommendationDecision({
        rec,
        status,
        reason: founderWhy,
        replacementMoveId,
        snapshotId: live.fingerprint,
        scoringConfigVersion: rec.scoringConfigVersion || live.scoringConfigVersion,
        today,
      }),
    });
  };

  const mark = (rec, status, founderWhy = '', replacementMoveId = '') => {
    const existing = (state.bridgeRecommendations || []).find((r) => r.id === rec.id);
    if (existing) {
      patch('bridgeRecommendations', rec.id, { status, founderWhy, replacementMoveId, respondedAt: today });
    } else {
      add('bridgeRecommendations', {
        ...rec,
        status,
        founderWhy,
        replacementMoveId,
        respondedAt: today,
        date: today,
        briefingId: rec.briefingId || '',
      });
    }
    logDecision(rec, status, founderWhy, replacementMoveId);
  };

  const acceptOne = (rec) => {
    const action = rec.payload?.actionId
      ? (state.top3 || []).find((a) => a.id === rec.payload.actionId)
      : null;
    const result = applyAccept(rec, { today, action });
    if (result.modal === 'designate') {
      setModalRec(rec);
      setModal(d.funnel.primary ? 'replace' : 'designate');
      return;
    }
    if (result.modal === 'exception') {
      setModalRec(rec);
      setModal('exception');
      return;
    }
    runMutations(result.mutations);
    mark(rec, 'accepted');
    fireCrmWriteback(result.writeback);
  };

  const acceptAll3 = () => {
    if (!canAcceptAll3) return;
    for (const rec of pendingPromote) {
      const action = rec.payload?.actionId
        ? (state.top3 || []).find((a) => a.id === rec.payload.actionId)
        : null;
      const result = applyAccept(rec, { today, action });
      if (result.modal) {
        setModalRec(rec);
        setModal(result.modal === 'designate' && d.funnel.primary ? 'replace' : result.modal);
        continue;
      }
      runMutations(result.mutations);
      mark(rec, 'accepted');
      fireCrmWriteback(result.writeback);
    }
  };

  const candidates = (d.funnel.candidates || []).map((c) => ({
    ...c,
    score: candidateScore(c).total,
    evidenceLevel: c.evidenceLevel || 'E0',
  }));

  const finishPrimary = (rec) => {
    mark(rec, 'accepted');
    setModal(null);
    setModalRec(null);
  };

  return (
    <div className="bridge bridge-gov">
      <header className="head">
        <h3>The Bridge</h3>
        <span className="sub">Operating brain · deterministic truth, agentic plan</span>
      </header>

      {autopilotIdle && (
        <Banner tone="amber">Autopilot idle — showing the deterministic briefing until a Plan Version exists. Enable a provider and run Re-study.</Banner>
      )}
      {(planStatus?.error || planStatus?.lastError) && (
        <Banner tone="bad">{planStatus.error || planStatus.lastError}</Banner>
      )}
      {currentPlan?.rejected && (
        <Banner tone="bad">
          Plan version rejected by policy{currentPlan.why ? `: ${currentPlan.why}` : '. Re-study after fixing the model output.'}
        </Banner>
      )}
      {planStatus?.freshness === 'stale' && (
        <Banner tone="amber">PLAN STALE — newer commercial state awaiting re-study</Banner>
      )}
      {planStatus?.freshness === 'fresh' && currentPlan && (
        <p className="bridge-copy dim tiny">
          Plan {String(currentPlan.id || '').slice(0, 18)} · {planStatus.inputAsOf ? String(planStatus.inputAsOf).replace('T', ' ').slice(0, 16) : ''} · {planStatus.constraint?.title || currentPlan.constraint?.title}
        </p>
      )}

      {briefing.drift?.length > 0 && (
        <div className="bridge-block">
          {briefing.drift.map((dr) => (
            <div className="bridge-drift" key={dr.id}>
              <strong>DRIFT DETECTED</strong>
              <p>{dr.text}</p>
            </div>
          ))}
        </div>
      )}

      <section className="bridge-block">
        <div className="eyebrow">Current Constraint</div>
        <h2>{currentPlan?.constraint?.title || briefing.constraint?.title}</h2>
        <p className="bridge-diag">
          <strong>Why.</strong> {currentPlan?.why || briefing.diagnosis}
        </p>
        {currentPlan?.whatChanged ? (
          <p className="bridge-copy"><strong>What changed.</strong> {currentPlan.whatChanged}</p>
        ) : null}
        {currentPlan?.whatItMeans ? (
          <p className="bridge-copy"><strong>What it means.</strong> {currentPlan.whatItMeans}</p>
        ) : null}
        {currentPlan?.whatNext ? (
          <p className="bridge-copy"><strong>What next.</strong> {currentPlan.whatNext}</p>
        ) : null}
        {currentPlan?.abstained && !currentPlan?.rejected ? (
          <p className="bridge-copy">Constraint not assessable. Missing: {(currentPlan.missingInformation || []).join(', ') || 'recorded commercial facts'}.</p>
        ) : null}
      </section>

      {init && (
        <section className="bridge-block">
          <div className="eyebrow">Initialization</div>
          <p className="bridge-copy">
            <strong>{init.complete}/{init.total} complete</strong>
            {init.remainingMinutes > 0 ? ` · ~${init.remainingMinutes} min remaining` : ' · ready'}
          </p>
          <ul className="bridge-list">
            {init.items.map((item) => (
              <li key={item.id}>
                {item.done ? 'Done' : 'Open'} — {item.label}
              </li>
            ))}
          </ul>
          {!init.commercialReady && (
            <p className="bridge-copy">
              Commercial priorities regenerate after the Top 50 import. Today’s AI Plan stays empty until accounts exist.
            </p>
          )}
        </section>
      )}

      <section className="bridge-block">
        <div className="eyebrow">Recommended</div>
        {pendingPromote.length === 0 ? (
          <p className="bridge-copy">
            {init && !init.commercialReady
              ? 'No commercial recommendations until accounts are imported.'
              : 'No open commercial moves to promote. Add a named next step, then accept it here.'}
          </p>
        ) : (
          pendingPromote.map((rec, i) => (
            <div className="bridge-rec" key={rec.id}>
              <span className="n">{i + 1}</span>
              <div style={{ flex: 1 }}>
                <div className="t">{rec.payload?.text}</div>
                <div className="why">
                  <strong>Why?</strong> {rec.why}
                </div>
                <div className="meta">
                  {rec.payload?.linkedLabel ? <span>Linked: {rec.payload.linkedLabel}</span> : null}
                  {rec.payload?.expectedEvidence ? (
                    <span>
                      Evidence: <EvidenceBadge level={rec.payload.expectedEvidence} />
                    </span>
                  ) : null}
                  {rec.payload?.gateRelevance && rec.payload.gateRelevance !== 'none' ? (
                    <span>Gate: Day {rec.payload.gateRelevance}</span>
                  ) : null}
                </div>
                <div className="dim tiny">If delayed: {rec.payload?.delayConsequence}</div>
                <RecStatus
                  rec={rec}
                  hideAccept={!!currentPlan && rec.kind === 'promote_top3'}
                  onAccept={() => acceptOne(rec)}
                  onReject={() => setRejectId(rec.id)}
                  onOverride={() => setOverrideId(rec.id)}
                />
              </div>
            </div>
          ))
        )}
      </section>

      {pending.filter((r) => r.kind !== 'promote_top3').length > 0 && (
        <section className="bridge-block">
          <div className="eyebrow">Material recommendations</div>
          {pending
            .filter((r) => r.kind !== 'promote_top3')
            .map((rec) => (
              <div className="bridge-rec" key={rec.id}>
                <Chip>{rec.kind.replace(/_/g, ' ')}</Chip>
                <div style={{ flex: 1 }}>
                  <div className="t">{rec.payload?.text || rec.payload?.candidateName || rec.kind}</div>
                  <div className="why">
                    <strong>Why?</strong> {rec.why}
                  </div>
                  <RecStatus rec={rec} onAccept={() => acceptOne(rec)} onReject={() => setRejectId(rec.id)} onOverride={() => setOverrideId(rec.id)} />
                </div>
              </div>
            ))}
        </section>
      )}

      {currentPlan && (currentPlan.governanceProposals || []).length > 0 && (
        <section className="bridge-block">
          <div className="eyebrow">Govern — founder decision required</div>
          {(currentPlan.governanceProposals || []).map((g, i) => (
            <div className="bridge-rec" key={`${g.kind}-${i}`}>
              <Chip>{String(g.kind || '').replace(/_/g, ' ')}</Chip>
              <div style={{ flex: 1 }}>
                <div className="t">{g.payload?.text || g.whyNow}</div>
                <div className="why"><strong>Why?</strong> {g.whyNow}</div>
              </div>
            </div>
          ))}
        </section>
      )}

      {versions.length > 0 && (
        <section className="bridge-block">
          <div className="eyebrow">How the AI is maneuvering</div>
          {versions.slice(0, 8).map((v) => (
            <div className="bridge-copy" key={v.id} style={{ marginBottom: 8 }}>
              <strong>{String(v.createdAt || '').replace('T', ' ').slice(0, 16)}</strong>
              {' · '}
              {v.trigger}
              {v.constraint?.title ? ` · ${v.constraint.title}` : ''}
              {v.rejected ? ' · rejected by policy' : v.abstained ? ' · abstained' : ''}
              {v.rollbackOf ? ` · rollback of ${v.rollbackOf}` : ''}
              <div className="dim tiny">{v.whatChanged}</div>
              {v.rejected && (v.validationErrors || []).length ? (
                <div className="dim tiny">{(v.validationErrors || []).join('; ')}</div>
              ) : null}
              <button className="btn sm ghost" onClick={() => { setRollbackId(v.id); setRollbackWhy(''); }}>Rollback</button>
            </div>
          ))}
        </section>
      )}

      <footer className="bridge-actions">
        <button
          className="btn primary"
          disabled={!!planBusy}
          onClick={async () => {
            setPlanBusy('restudy');
            try {
              await api.planRestudy({ trigger: 'manual', force: true });
              if (reload) await reload();
              const next = await api.planStatus();
              setPlanStatus(next);
            } catch (err) {
              setPlanStatus((p) => ({ ...(p || {}), error: err.message }));
            } finally {
              setPlanBusy('');
            }
          }}
        >
          {planBusy === 'restudy' ? 'Re-studying…' : 'Re-study'}
        </button>
        <button className="btn" onClick={() => setAskOpen(true)}>
          Ask The Bridge
        </button>
      </footer>

      {rollbackId && (
        <Modal
          title="Rollback Plan Version"
          sub="Restores the previous plan mutations. Founder commitments are not rewritten."
          onClose={() => { setRollbackId(''); setRollbackWhy(''); }}
          footer={
            <>
              <button className="btn" onClick={() => { setRollbackId(''); setRollbackWhy(''); }}>Cancel</button>
              <button
                className="btn primary"
                disabled={!rollbackWhy.trim() || !!planBusy}
                onClick={async () => {
                  setPlanBusy('rollback');
                  try {
                    await api.rollbackPlanVersion(rollbackId, rollbackWhy.trim());
                    if (reload) await reload();
                    setPlanStatus(await api.planStatus());
                  } finally {
                    setPlanBusy('');
                    setRollbackId('');
                    setRollbackWhy('');
                  }
                }}
              >
                Rollback
              </button>
            </>
          }
        >
          <Area label="Reason" value={rollbackWhy} onChange={setRollbackWhy} rows={3} placeholder="Required." />
        </Modal>
      )}
      {overrideId && (
        <Modal
          title="Override recommendation"
          sub="Replacement move and reason are required. The replacement is tested against the same eligibility rules."
          onClose={() => { setOverrideId(null); setOverrideWhy(''); setOverrideReplacement(''); setOverrideError(''); }}
          footer={
            <>
              <button className="btn" onClick={() => { setOverrideId(null); setOverrideWhy(''); setOverrideReplacement(''); setOverrideError(''); }}>Cancel</button>
              <button
                className="btn primary"
                disabled={!overrideWhy.trim() || !overrideReplacement.trim()}
                onClick={() => {
                  const rec = recs.find((r) => r.id === overrideId) || pending.find((r) => r.id === overrideId);
                  if (rec) {
                    const replacement = newActionFromText(overrideReplacement, today);
                    const eligibility = evaluateEligibility(replacement, { exceptions: state.exceptions, state });
                    if (!eligibility.eligible) {
                      setOverrideError(`Replacement is not eligible: ${eligibility.reasons.join(', ')}.`);
                      return;
                    }
                    add('top3', { ...replacement, id: uid('t3'), selectedForToday: true, status: 'Today', source: 'Manual' });
                    mark(rec, 'overridden', overrideWhy, replacement.text);
                  }
                  setOverrideId(null);
                  setOverrideWhy('');
                  setOverrideReplacement('');
                  setOverrideError('');
                }}
              >
                Save override
              </button>
            </>
          }
        >
          <Area label="Replacement move" value={overrideReplacement} onChange={(value) => { setOverrideReplacement(value); setOverrideError(''); }} rows={2} placeholder="The move that should be Today instead." />
          <Area label="Reason" value={overrideWhy} onChange={setOverrideWhy} rows={3} placeholder="Required. Why this replacement is eligible." />
          {overrideError ? <p className="bridge-copy" style={{ color: 'var(--red)' }}>{overrideError}</p> : null}
        </Modal>
      )}

      {rejectId && (
        <Modal
          title="Reject recommendation"
          sub="A short reason is required."
          onClose={() => { setRejectId(null); setRejectWhy(''); }}
          footer={
            <>
              <button className="btn" onClick={() => { setRejectId(null); setRejectWhy(''); }}>Cancel</button>
              <button
                className="btn primary"
                disabled={!rejectWhy.trim()}
                onClick={() => {
                  const rec = recs.find((r) => r.id === rejectId) || pending.find((r) => r.id === rejectId);
                  if (rec) mark(rec, 'rejected', rejectWhy);
                  setRejectId(null);
                  setRejectWhy('');
                }}
              >
                Reject
              </button>
            </>
          }
        >
          <Area label="Reason" value={rejectWhy} onChange={setRejectWhy} rows={3} placeholder="Required." />
        </Modal>
      )}

      {askOpen && <AskTheBridge briefing={briefing} onClose={() => setAskOpen(false)} />}

      {modal === 'exception' && (
        <ExceptionForm
          prefill={modalRec?.payload}
          onClose={() => { setModal(null); if (modalRec) mark(modalRec, 'rejected'); setModalRec(null); }}
          onSubmitted={() => { if (modalRec) mark(modalRec, 'accepted'); setModal(null); setModalRec(null); }}
        />
      )}

      {modal === 'designate' && (
        <DesignatePrimaryModal
          candidates={candidates}
          current={null}
          onClose={() => { setModal(null); setModalRec(null); }}
          onSave={(f) => {
            const chosen = candidates.find((c) => c.id === f.candidateId);
            set('opportunities', withPrimary(state.opportunities, { candidateId: f.candidateId, backupId: f.backupId, today }));
            set('d001.primaryOpportunityId', f.candidateId);
            set('d001.exists', true);
            add('decisions', {
              id: uid('dec'),
              date: today,
              type: 'D001 selection',
              question: 'Which opportunity becomes Primary D001?',
              options: f.alternatives,
              evidence: f.evidence,
              decision: `${chosen?.name || 'Selected candidate'} designated Primary D001. Primary opportunity changed based on evidence. The underlying strategy remains locked.`,
              reviewDate: f.reviewDate,
            });
            finishPrimary(modalRec);
          }}
        />
      )}

      {modal === 'replace' && (
        <ReplacePrimaryModal
          current={d.funnel.primary}
          candidates={candidates}
          onClose={() => { setModal(null); setModalRec(null); }}
          onSave={(f) => {
            const old = d.funnel.primary;
            const chosen = candidates.find((c) => c.id === f.candidateId);
            set(
              'opportunities',
              withReplacedPrimary(withPrimary(state.opportunities, { candidateId: f.candidateId, backupId: f.backupId, today }), {
                oldId: old?.id,
                disposition: f.disposition,
                today,
              }),
            );
            set('d001.primaryOpportunityId', f.candidateId);
            add('decisions', {
              id: uid('dec'),
              date: today,
              type: 'D001 selection',
              question: `Replace Primary D001 (${old?.name || 'current'})?`,
              options: `Keep ${old?.name || 'current'} / promote ${chosen?.name || 'candidate'}`,
              evidence: `${f.reason} · ${f.evidence}`,
              decision: `${chosen?.name} becomes Primary D001. Primary opportunity changed based on evidence. The underlying strategy remains locked.`,
              reviewDate: f.reviewDate,
            });
            finishPrimary(modalRec);
          }}
        />
      )}
    </div>
  );
}

function RecStatus({ rec, onAccept, onReject, onOverride, hideAccept = false }) {
  const status = rec?.status || 'pending';
  if (status === 'accepted') return <Chip tone="green">Accepted</Chip>;
  if (status === 'rejected') return <Chip>Rejected</Chip>;
  if (status === 'overridden') return <Chip tone="amber">Overridden{rec.founderWhy ? ` — ${rec.founderWhy}` : ''}</Chip>;
  return (
    <div className="row tight" style={{ marginTop: 8 }}>
      {!hideAccept && <button className="btn sm primary" onClick={onAccept}>Accept</button>}
      <button className="btn sm ghost" onClick={onOverride}>Override</button>
      <button className="btn sm ghost" onClick={onReject}>Reject</button>
    </div>
  );
}

function AskTheBridge({ briefing, onClose }) {
  const { state, add, today } = useStore();
  const [q, setQ] = useState('');
  const [answer, setAnswer] = useState('');
  const [structured, setStructured] = useState(null);
  const [busy, setBusy] = useState(false);
  const [providerLabel, setProviderLabel] = useState('');
  const [error, setError] = useState('');
  const [source, setSource] = useState('');

  useEffect(() => {
    api.listBridgeProviders?.()
      .then((res) => {
        const active = (res.providers || []).find((p) => p.enabled && p.hasKey);
        if (active) setProviderLabel(`${active.name} · ${active.model || active.defaultModel || ''}`);
      })
      .catch(() => {});
  }, []);

  const ask = async (question) => {
    const text = String(question || q).trim();
    if (!text) return;
    setBusy(true);
    setQ(text);
    setError('');
    setSource('');
    const localStruct = structureAskAnswer(text, briefing);
    const local = localStruct.text;
    try {
      const res = await api.bridgeAsk({
        question: text,
        briefing: compactAskContext(briefing),
        snapshotId: briefing.fingerprint,
      });
      if (res?.source === 'rejected') {
        setSource('rejected');
        setError((res.validationErrors || []).join('; ') || res.answer);
        setStructured(null);
        setAnswer(typeof res?.answer === 'string' ? res.answer : '');
        return;
      }
      const buckets = mapAskStructured(res?.structured, briefing);
      const hasBuckets = buckets.facts.length || buckets.inferences.length || buckets.recommendations.length || buckets.missing.length;
      setSource('model');
      setStructured(hasBuckets ? buckets : null);
      setAnswer(typeof res?.answer === 'string' ? res.answer : buckets.text || '');
      if (!res?.ask) {
        add('bridgeAsks', { id: uid('ask'), date: today, question: text, answer: res?.answer || buckets.text, briefingId: '', snapshotId: briefing.fingerprint });
      }
    } catch (err) {
      setSource('local');
      setError(err.message || 'Ask The Bridge could not reach the model.');
      setStructured(localStruct);
      setAnswer(local);
      add('bridgeAsks', { id: uid('ask'), date: today, question: text, answer: local, briefingId: '', snapshotId: briefing.fingerprint });
    } finally {
      setBusy(false);
    }
  };

  const hasBuckets = structured && (
    (structured.facts || []).length
    || (structured.inferences || []).length
    || (structured.recommendations || []).length
    || (structured.missing || []).length
  );

  return (
    <Modal
      title="Ask The Bridge"
      sub={providerLabel ? `Secondary. ${providerLabel}` : 'Secondary. Answers from the briefing if no provider is configured.'}
      onClose={onClose}
      wide
      footer={
        <>
          <button className="btn" onClick={onClose}>Close</button>
          <button className="btn primary" disabled={busy || !q.trim()} onClick={() => ask()}>
            {busy ? 'Asking…' : 'Ask'}
          </button>
        </>
      }
    >
      <div className="row wrap" style={{ gap: 6, marginBottom: 10 }}>
        {(orgAskQuestions(state) || BRIDGE_ASK_QUESTIONS).map((s) => (
          <button key={s} className="btn sm ghost" onClick={() => ask(s)}>{s}</button>
        ))}
      </div>
      <Area label="Question" value={q} onChange={setQ} rows={2} />
      {error ? (
        <Banner tone={source === 'local' ? 'amber' : 'bad'}>
          {source === 'local' ? `AI did not answer. ${error}` : error}
        </Banner>
      ) : null}
      {source === 'local' ? <div className="dim tiny">From the briefing — not from the model.</div> : null}
      {hasBuckets && (
        <div className="bridge-answer">
          <AskBucket title="Recorded fact" items={structured.facts} />
          <AskBucket title="Inference" items={structured.inferences} />
          <AskBucket title="Recommendation" items={structured.recommendations} />
          <AskBucket title="Missing information" items={(structured.missing || []).map((m) => ({ claim: typeof m === 'string' ? m : m.claim }))} />
        </div>
      )}
      {answer && (!hasBuckets || source === 'rejected') ? <div className="bridge-answer">{answer}</div> : null}
    </Modal>
  );
}

function AskBucket({ title, items }) {
  if (!items?.length) return null;
  return (
    <div style={{ marginBottom: 10 }}>
      <div className="eyebrow">{title}</div>
      {items.map((it, i) => (
        <p key={i} className="bridge-copy">
          {it.claim || it}
          {it.recordRefs?.length ? <span className="dim tiny"> · {it.recordRefs.join(', ')}</span> : null}
        </p>
      ))}
    </div>
  );
}

export function Specialists() {
  const { state, d, add, today } = useStore();
  const [open, setOpen] = useState(false);
  const [key, setKey] = useState(SPECIALISTS[0].key);
  const result = useMemo(() => {
    try {
      return runSpecialist(key, state, d);
    } catch (err) {
      return { sections: [{ title: 'Unavailable', items: [String(err.message || err)] }], drafts: [], disclaimer: '' };
    }
  }, [key, state, d]);

  return (
    <Card
      title="Specialist views"
      sub="Outside the governor. Drafts do not affect ranking until you Accept them into Evidence."
      right={
        <button className="btn sm ghost" onClick={() => setOpen((v) => !v)}>
          {open ? 'Hide' : 'Show'}
        </button>
      }
    >
      {open && (
        <>
          <div className="row tight" style={{ marginBottom: 10 }}>
            {SPECIALISTS.map((s) => (
              <button
                key={s.key}
                className={`btn sm ${s.key === key ? 'primary' : 'ghost'}`}
                onClick={() => setKey(s.key)}
              >
                {s.name}
              </button>
            ))}
          </div>
          <div className="specialist" style={{ border: 0 }}>
            <div className="sp-head" style={{ border: 0, padding: 0, marginBottom: 10 }}>
              <h4>{result.name || SPECIALISTS.find((s) => s.key === key)?.name}</h4>
              <p>{result.blurb || SPECIALISTS.find((s) => s.key === key)?.blurb}</p>
              {result.disclaimer ? <p className="dim tiny">{result.disclaimer}</p> : null}
            </div>
            <div className="sp-body" style={{ padding: 0 }}>
              {(result.sections || []).map((sec) => (
                <div key={sec.title}>
                  <h5>{sec.title}</h5>
                  <ul>
                    {sec.items.map((it, i) => (
                      <li key={i}>{it}</li>
                    ))}
                  </ul>
                </div>
              ))}
              {(result.drafts || []).length > 0 && (
                <div>
                  <h5>Sourced drafts — Accept into state</h5>
                  {result.drafts.map((draft) => (
                    <div className="row" key={draft.id} style={{ marginBottom: 8 }}>
                      <span style={{ flex: 1 }}>{draft.title}</span>
                      <button
                        className="btn sm"
                        onClick={() => add('evidence', { id: uid('ev'), ...draftToEvidenceRecord(draft, today) })}
                      >
                        Accept into Evidence
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </Card>
  );
}

/** Header launcher — Ask The Bridge without Diagnostics machinery. */
export function AskTheBridgeButton() {
  const { state, d } = useStore();
  const [open, setOpen] = useState(false);
  const lastCloseout = useMemo(() => {
    const rows = [...(state.closeouts || [])].sort((a, b) => String(b.date).localeCompare(String(a.date)));
    return rows[0] || null;
  }, [state.closeouts]);
  const briefing = useMemo(
    () => composeBriefing(state, d, { lastCloseout, overrides: state.bridgeRecommendations || [] }),
    [state, d, lastCloseout],
  );
  return (
    <>
      <button type="button" className="btn sm primary" onClick={() => setOpen(true)}>
        Ask The Bridge
      </button>
      {open && <AskTheBridge briefing={briefing} onClose={() => setOpen(false)} />}
    </>
  );
}
